import React, { CSSProperties, FC, ReactNode } from 'react';
import { Dialog } from '@material-ui/core';
import { rgba } from 'polished';
import { colors } from 'src/config/colors';
import { useMediaQuery } from 'src/common/migration/useMediaQuery';
import { media, widthBreakpoints } from 'src/config/breakpoints';
import styled, { css } from 'styled-components';
import { layers } from 'src/config/layers';
import CloseIcon from 'src/assets/icons/CloseIcon';
import { Button, ButtonWithLoader, CleanButton } from 'src/common/components/Button';
import { heading } from 'src/config/typography';
import { LogoVerticalCentered } from 'src/common/images/LogoVerticalCentered';
import { LogoHorizontal } from 'src/common/images/LogoHorizontal';

export interface ConfirmationDialogProps {
  title: ReactNode;
  question: ReactNode;
  image?: ReactNode;

  confirmLabel: string;
  onConfirm: () => void;
  confirmLoading?: boolean;
  isFirstButtonPrimary?: boolean;

  cancelLabel: string;
  onCancel: () => void;
}

const StyledDialog = styled(Dialog)`
  .MuiDialog-scrollPaper {
    align-items: flex-start;
  }

  .MuiPaper-rounded:not(.MuiDialog-paperFullScreen) {
    border-radius: 0.75rem;
  }

  .MuiDialog-paper {
    max-width: (100% - 2.25rem);
    color: ${colors.blue};

    ${media.w.min.px576(css`
      margin-top: 3.75rem;
      margin-bottom: 1.5rem;
    `)}

    ${media.w.min.px1200(css`
      margin-top: 4.375rem;
      max-width: 62.5rem;
    `)}
  }
`;

const backdropStyleProps: CSSProperties = {
  backgroundColor: rgba(colors.blue, 0.9),
};

const TopBar = styled.div`
  padding: 1.4375rem;
  display: flex;
  justify-content: flex-end;
`;

const DialogBody = styled.div`
  padding: 1.1875rem 1.625rem 4rem;

  ${media.w.min.px576(css`
    padding: 2.1875rem 7.25rem 4.3125rem;
  `)}

  ${media.w.min.px1200(css`
    padding: 2.1875rem 5.625rem 9.375rem 8rem;
  `)}
`;

const ContentLayout = styled.div`
  display: grid;
  grid-template-areas:
    'image'
    'content';
  grid-gap: 2.25rem;

  ${media.w.min.px992(css`
    grid-template-columns: 1fr minmax(17.5rem, 18.75rem);
    grid-gap: 1rem;
    grid-template-areas: 'content image';
  `)}
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2.5rem;

  ${media.w.min.px576(css`
    margin-bottom: 3.75rem;
  `)}

  ${media.w.min.px992(css`
    justify-content: unset;
    margin-bottom: 1.25rem;
  `)}
`;

const Heading = styled.h1`
  color: ${colors.red};
  ${heading.h1};

  text-align: center;

  ${media.w.min.px992(css`
    text-align: unset;
  `)}
`;

const Content = styled.div`
  grid-area: content;
`;

const Description = styled.div`
  margin-bottom: 2.5rem;
`;

const ConfirmationContainer = styled.div`
  width: fit-content;

  ${media.w.max.px992(css`
    margin: auto;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  `)}
`;

const ConfirmationQuestion = styled.div`
  font-weight: 700;
  margin-bottom: 0.625rem;

  ${media.w.max.px992(css`
    text-align: center;
  `)}
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column-reverse;

  & > * {
    margin-bottom: 0.625rem;
  }

  ${media.w.min.px576(css`
    display: grid;

    justify-content: space-between;
    grid-auto-flow: column;
    grid-auto-columns: min-content;
  `)}

  ${media.w.min.px992(css`
    justify-content: unset;
    grid-gap: 1.5rem;
  `)}
`;

const DialogImage = styled.div`
  grid-area: image;
  display: flex;
  justify-content: center;

  ${media.w.min.px992(css`
    margin-bottom: 1.25rem;
  `)}

  // workaround for safari/edge svg flex child (without explicit size)
  // https://github.com/philipwalton/flexbugs/issues/184
  & > * {
    flex: 1;
  }
`;

export const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
  title,
  question,
  image,
  children,
  confirmLabel,
  confirmLoading = false,
  cancelLabel,
  isFirstButtonPrimary = false,
  onConfirm,
  onCancel,
}) => {
  const isMobile = useMediaQuery({ maxWidth: widthBreakpoints.px576 });

  return (
    <StyledDialog
      onClose={() => null}
      open={true}
      BackdropProps={{ style: backdropStyleProps }}
      fullScreen={isMobile}
      fullWidth
      maxWidth={false}
      style={{ zIndex: layers.overMobileMenu }}
    >
      <div>
        <TopBar>
          <CleanButton onClick={onCancel}>
            <CloseIcon size={18} />
          </CleanButton>
        </TopBar>
        <DialogBody>
          <LogoContainer>
            {isMobile && <LogoVerticalCentered />}
            {!isMobile && <LogoHorizontal />}
          </LogoContainer>

          <ContentLayout>
            <Content>
              <Heading>{title}</Heading>
              <Description>{children}</Description>
              <ConfirmationContainer>
                <ConfirmationQuestion>{question}</ConfirmationQuestion>
                <ButtonGroup>
                  <Button onClick={onCancel} kind={isFirstButtonPrimary ? 'primary' : 'border'}>
                    {cancelLabel}
                  </Button>
                  <ButtonWithLoader
                    onClick={onConfirm}
                    kind={isFirstButtonPrimary ? 'border' : 'primary'}
                    loading={confirmLoading}
                  >
                    {confirmLabel}
                  </ButtonWithLoader>
                </ButtonGroup>
              </ConfirmationContainer>
            </Content>
            <DialogImage>{image}</DialogImage>
          </ContentLayout>
        </DialogBody>
      </div>
    </StyledDialog>
  );
};
